import * as React from "react"
import { ChartData } from "../subsection"
import * as d3 from "d3"
import { fitChartWidth, resolveChartDefaultWidth } from "./chart_size"


interface PieChartProps {
    chartData: ChartData
    compact?: boolean
    panelWidth?: number
    panelHeight?: number
    svgId?: string
}
export const GeneratePieChart = ({ chartData, compact, panelWidth, panelHeight, svgId = "pie" }: PieChartProps) => {
    const [hoveredSlice, setHoveredSlice] = React.useState<number | null>(null)
    const [hasAnimated, setHasAnimated] = React.useState(false)
    const [isAnimating, setIsAnimating] = React.useState(false)
    const svgRef = React.useRef<SVGSVGElement>(null)

    const data = chartData.count
    const colors = chartData.colors
    const size = fitChartWidth(compact, panelWidth, resolveChartDefaultWidth(compact, panelWidth, 170, 260, 480), 96, panelHeight)
    const [h, w] = [size, size]
    const m = compact ? 14 : 22
    const radius = Math.min(h, w) / 2 - m

    const pieGenerator = d3.pie().value((d) => (d as any).value)
    const arrayOfObj = Array.from(data, ([name, value]) => ({ name, value }))
    const pie = pieGenerator(arrayOfObj as any)

    // Animate pie slices using D3
    React.useEffect(() => {
        if (!svgRef.current || hasAnimated) return

        const svg = d3.select(svgRef.current)
        const g = svg.select('g')
        
        // Bind data to paths
        const paths = g.selectAll('path')
            .data(pie)
        
        // Set initial state (all slices at center - innerRadius = outerRadius)
        paths.attr('d', function(d: any) {
            const arc = d3.arc()
                .innerRadius(0)
                .outerRadius(0) // Start from center
            return arc({
                startAngle: d.startAngle,
                endAngle: d.endAngle,
                innerRadius: 0,
                outerRadius: 0
            }) as string
        })

        // Animate to final state with stagger - growing from center outwards
        setIsAnimating(true)
        paths.transition()
            .duration(800)
            .delay((d: any, i: number) => i * 100)
            .ease(d3.easeCubicOut)
            .attrTween('d', function(d: any) {
                const interpolate = d3.interpolate(
                    { startAngle: d.startAngle, endAngle: d.endAngle, innerRadius: 0, outerRadius: 0 },
                    { startAngle: d.startAngle, endAngle: d.endAngle, innerRadius: 0, outerRadius: radius }
                )
                return function(t: number) {
                    const arc = d3.arc()
                        .innerRadius(0)
                        .outerRadius(interpolate(t).outerRadius)
                    return arc(interpolate(t)) as string
                }
            })
            .on('end', () => {
                setHasAnimated(true)
                setIsAnimating(false)
            })
    }, [pie, radius, hasAnimated, h, w])

    const arcPathGenerator = d3.arc()
    const arcs = pie.map((p, i) => ({
        path: arcPathGenerator({
            innerRadius: 0,
            outerRadius: radius,
            startAngle: p.startAngle,
            endAngle: p.endAngle,
        }),
        color: (colors ? Array.from(colors, ([i, j]) => j)[i] : [0, 0, 0, 0]), // TODO: no color exists, Boolean data, retrieve color from elsewhere.
        data: arrayOfObj[i]
    }));

    return (
        <div style={{ textAlign: 'center', maxWidth: "100%", overflow: "hidden" }}>
            <svg ref={svgRef} id={svgId} width={w} height={h} style={{ display: "block", maxWidth: "100%", margin: "0 auto" }}>
                <g transform={`translate(${w / 2}, ${h / 2})`}>
                    {arcs.map((arc, i) => {
                        const isHovered = hoveredSlice === i
                        const labelArc = d3.arc()
                            .innerRadius(radius * 0.7)
                            .outerRadius(radius * 0.7)
                        
                        const labelPos = labelArc.centroid({
                            startAngle: pie[i].startAngle,
                            endAngle: pie[i].endAngle,
                            innerRadius: 0,
                            outerRadius: radius
                        })

                        return (
                            <g key={i}>
                                <path 
                                    d={arc.path as any} 
                                    stroke="lightgrey" 
                                    fill={hoveredSlice === i ? `rgb(${Math.min(255, arc.color[0] + 30)}, ${Math.min(255, arc.color[1] + 30)}, ${Math.min(255, arc.color[2] + 30)})` : `rgb(${arc.color[0]}, ${arc.color[1]}, ${arc.color[2]})`}
                                    style={{
                                        cursor: 'pointer',
                                        transition: 'fill 0.2s ease'
                                    }}
                                    onMouseEnter={() => {
                                        if (!isAnimating) {
                                            setHoveredSlice(i)
                                        }
                                    }}
                                    onMouseLeave={() => {
                                        if (!isAnimating) {
                                            setHoveredSlice(null)
                                        }
                                    }}
                                />
                                {isHovered && (
                                    <text
                                        x={labelPos[0]}
                                        y={labelPos[1]}
                                        fontSize={compact ? 8 : 10}
                                        fill="black"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        style={{
                                            pointerEvents: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {arc.data.value.toFixed(2)}
                                    </text>
                                )}
                            </g>
                        )
                    })}
                </g>
            </svg>
        </div>
    )
}